import Flex from '@Components/Bases/Flex';
import { EditReportFormValues, IReport, useEditReport } from '@Domains/Reports';
import { useDisclosure } from '@hooks/useDisclosure';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import { Button, Dialog, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DateAdapter from '@Utils/DateAdapter';
import { HttpStatusCode } from 'axios';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import DurationYearForm from '../DurationYearForm';
import { mapPayloadEditReport, useSubmitDisabled } from '../utils';
import EditOperatingYearAction from './EditOperatingYearAction';

type EditOperatingYearModalProps = {
  reportDetail?: IReport;
  refetchProgress?: () => void;
};

const useEditReportForm = (reportDetail?: IReport) => {
  const defaultValues = {
    reportType: reportDetail?.reportType,
    startOperatingYear: reportDetail?.startOperatingYear
      ? dayjs().year(Number(reportDetail.startOperatingYear) - 543)
      : null,
    endOperatingYear: reportDetail?.endOperatingYear
      ? dayjs().year(Number(reportDetail.endOperatingYear) - 543)
      : null,
  };

  return useForm<EditReportFormValues>({
    mode: 'onChange',
    defaultValues,
  });
};

const EditOperatingYearModal = ({
  reportDetail,
  refetchProgress,
}: EditOperatingYearModalProps) => {
  const [opened, { open, close }] = useDisclosure();
  const [, executeEditReport] = useEditReport();
  const form = useEditReportForm(reportDetail);
  const values = form.watch();
  const isSubmitDisabled = useSubmitDisabled(
    values.reportType,
    values.startOperatingYear,
    values.endOperatingYear
  );
  const isPublished = Boolean(reportDetail?.publishedDate);

  const handleOpen = () => {
    form.reset({
      reportType: reportDetail?.reportType,
      startOperatingYear: reportDetail?.startOperatingYear
        ? dayjs().year(Number(reportDetail.startOperatingYear) - 543)
        : null,
      endOperatingYear: reportDetail?.endOperatingYear
        ? dayjs().year(Number(reportDetail.endOperatingYear) - 543)
        : null,
    });
    open();
  };

  const handleSubmit = async (data: EditReportFormValues) => {
    const payload = mapPayloadEditReport(data);
    const { startOperatingYear, endOperatingYear, reportType } = payload;

    const response = await executeEditReport({
      data: {
        startOperatingYear,
        endOperatingYear,
        id: reportDetail?.id,
        reportType,
      },
    });

    if (response.status === HttpStatusCode.Ok) {
      refetchProgress?.();
      close();
    }
  };

  return (
    <LocalizationProvider dateAdapter={DateAdapter}>
      <Button
        color="info"
        variant="text"
        sx={{ fontWeight: 'regular' }}
        onClick={handleOpen}
        disabled={isPublished}
      >
        ตั้งค่า
      </Button>

      <FormProvider {...form}>
        <Dialog
          open={opened}
          onClose={close}
          maxWidth="sm"
          slotProps={{
            paper: {
              sx: {
                width: '520px',
                minWidth: '520px',
              },
            },
          }}
        >
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <Flex flexDirection={'column'} p={2} gap={2} width="100%">
              <Flex gap={1.5}>
                <AssignmentLateOutlinedIcon
                  fontSize="large"
                  sx={{ color: '#F68809' }}
                />
                <Typography fontWeight="bold">
                  แก้ไขแบบรายงาน Corporate Value Up
                </Typography>
              </Flex>
              <DurationYearForm />
            </Flex>
            <EditOperatingYearAction
              onClose={close}
              isDisabled={isSubmitDisabled}
            />
          </form>
        </Dialog>
      </FormProvider>
    </LocalizationProvider>
  );
};

export default EditOperatingYearModal;
